// ============================================================================
// Estilos da home — navegação faseada (handoff de design `design_handoff_home`).
//
// Nível 1 = card de módulo (quadrado, só ícone + nome). Nível 2 = modal com os
// itens agrupados. Não há nível 3. A home encaminha, não informa: nenhum
// contador técnico ("x telas em y módulos") aparece nesta tela (§1.1).
//
// Tokens conforme §5 do handoff. FC7: nada aqui escreve no banco, então não há
// botão azul de ação — o #4098ef aparece só como acento de foco/hover/ativo.
// ============================================================================
import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

const PRIMARY = '#4098ef';
const PRIMARY_ON_WHITE = '#2f7bd8';
const FOCUS_RING = '0 0 0 3px rgba(64, 152, 239, 0.18)';

// ─── Movimento ──────────────────────────────────────────────────────────────
// A home troca de conteúdo enquanto o usuário digita (grade de módulos ⇄
// resultados). Sem transição, cada tecla produz um corte seco e o usuário não
// percebe o que mudou. A entrada é curta e discreta — 220ms com deslocamento de
// 6px: perceptível, sem atrasar quem já sabe o destino.
const riseIn = keyframes`
   from {
      opacity: 0;
      transform: translateY(6px);
   }
   to {
      opacity: 1;
      transform: translateY(0);
   }
`;

const enterAnimation = css`
   animation: ${riseIn} 220ms ease both;

   @media (prefers-reduced-motion: reduce) {
      animation: none;
   }
`;

// Texto acessível apenas para leitor de tela (§6 — o ponto de "Novo" precisa
// de rótulo textual). Não existia utilitário equivalente no projeto.
export const srOnly = `
   position: absolute;
   width: 1px;
   height: 1px;
   padding: 0;
   margin: -1px;
   overflow: hidden;
   clip: rect(0, 0, 0, 0);
   white-space: nowrap;
   border: 0;
`;

export const HomeContainer = styled.div`
   width: 100%;
   max-width: 1120px;
   margin: 0 auto;
   padding: 44px 24px;

   @media (max-width: 900px) {
      padding: 24px 16px;
   }
`;

// ─── Saudação ───────────────────────────────────────────────────────────────
export const Greeting = styled.header`
   margin-bottom: 22px;

   h1 {
      margin: 0;
      font-size: 30px;
      font-weight: 700;
      line-height: 1.2;
      color: #fff;
   }

   p {
      margin: 6px 0 0;
      font-size: 14px;
      color: #bfbfbf;
   }

   @media (max-width: 900px) {
      h1 {
         font-size: 24px;
      }
   }
`;

// ─── Busca ──────────────────────────────────────────────────────────────────
export const SearchWrap = styled.div`
   max-width: 520px;
   margin-bottom: 26px;

   @media (max-width: 900px) {
      max-width: none;
   }
`;

// Contexto de posicionamento da lupa e do chip "Ctrl K".
//
// ⚠️ Precisa ser um wrapper PRÓPRIO, só do campo (2026-08-22). Enquanto o
// `position: relative` estava no `SearchWrap`, os dois elementos ancoravam em
// `top: 50%` do bloco INTEIRO — aí bastava o aviso de "mín. 3 caracteres"
// aparecer embaixo para o wrapper crescer e a lupa/chip descerem junto com o
// novo centro. Com o relative aqui, o centro é sempre o do input.
export const SearchField = styled.div`
   position: relative;

   .search-icon {
      position: absolute;
      top: 50%;
      left: 14px;
      transform: translateY(-50%);
      color: #a9a9a9;
      pointer-events: none;
      transition: color 200ms ease;
   }

   /* A lupa acompanha o foco — sinal de que o campo está ativo. */
   &:focus-within .search-icon {
      color: ${PRIMARY};
   }

   .search-hint {
      position: absolute;
      top: 50%;
      right: 12px;
      transform: translateY(-50%);
      padding: 2px 7px;
      border-radius: 6px;
      border: 1px solid rgba(255, 255, 255, 0.16);
      color: #a9a9a9;
      font-size: 11px;
      font-weight: 600;
      pointer-events: none;
      /* O atalho é dica para começar a busca; com texto digitado vira ruído,
         então some suavemente em vez de continuar competindo com o conteúdo. */
      opacity: ${({ $typing }) => ($typing ? 0 : 1)};
      transition: opacity 200ms ease;
   }

   @media (max-width: 900px) {
      .search-hint {
         display: none;
      }
   }
`;

export const SearchInput = styled.input`
   width: 100%;
   height: 44px;
   padding: 0 68px 0 42px;
   border-radius: 10px;
   border: 1px solid rgba(255, 255, 255, 0.16);
   background: rgba(255, 255, 255, 0.06);
   color: #e5e5e5;
   font-family: inherit;
   font-size: 14px;
   text-transform: none;
   transition: border-color 140ms ease, box-shadow 140ms ease;

   &::placeholder {
      color: #a9a9a9;
   }

   &:hover {
      border-color: rgba(255, 255, 255, 0.28);
   }

   &:focus {
      outline: none;
      border-color: ${PRIMARY};
      box-shadow: ${FOCUS_RING};
   }
`;

// ─── Seções ─────────────────────────────────────────────────────────────────
export const SectionLabel = styled.h2`
   margin: 0 0 12px;
   font-size: 10px;
   font-weight: 800;
   letter-spacing: 0.09em;
   text-transform: uppercase;
   color: #a9a9a9;
`;

// ─── Grade de módulos ───────────────────────────────────────────────────────
export const ModuleGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
   gap: 14px;

   @media (max-width: 900px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
   }
`;

// ⚠️ FC7 §G.4.1: `styled.button` PRECISA declarar o próprio `:hover` com
// `background` — `button:hover` do GlobalStyles (0,0,1,1) vence a classe do
// styled-component (0,0,1,0) e pintaria o card de azul no hover.
export const ModuleCardButton = styled.button`
   position: relative;
   aspect-ratio: 1 / 1;

   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 14px;
   padding: 16px;

   background: #fff;
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-radius: 12px;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
   cursor: pointer;
   font-family: inherit;
   transition: border-color 140ms ease, background 140ms ease;

   &:hover:not(:disabled) {
      background: rgba(64, 152, 239, 0.05);
      border-color: ${PRIMARY};
   }

   &:focus-visible {
      outline: none;
      border-color: ${PRIMARY};
      box-shadow: ${FOCUS_RING};
   }

   .module-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 64px;
      height: 64px;
      border-radius: 16px;
      background: rgba(64, 152, 239, 0.12);
      color: ${PRIMARY_ON_WHITE};
   }

   .module-name {
      font-size: 16px;
      font-weight: 700;
      line-height: 1.25;
      text-align: center;
      color: #111;
   }
`;

export const NewBadge = styled.span`
   position: absolute;
   top: 10px;
   right: 10px;
   padding: 2px 8px;
   border-radius: 999px;
   background: rgba(64, 152, 239, 0.12);
   color: ${PRIMARY_ON_WHITE};
   font-size: 10px;
   font-weight: 800;
   letter-spacing: 0.03em;
`;

// ─── Modal: grupos e itens ──────────────────────────────────────────────────
export const ModalScroll = styled.div`
   max-height: 68vh;
   overflow-y: auto;
   margin: -16px;
   padding: 16px;
`;

export const GroupBlock = styled.section`
   & + & {
      margin-top: 20px;
   }

   > h4 {
      margin: 0 0 8px;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: #8a8a8a;
   }
`;

export const ItemGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(292px, 1fr));
   gap: 8px;

   @media (max-width: 700px) {
      grid-template-columns: 1fr;
   }
`;

// Item = `<a>` real (§6): navega com Ctrl+clique e botão do meio.
// `min-height: 46px` e rótulo em até 2 linhas — nunca truncar (§2.2).
export const ItemLink = styled(Link)`
   display: flex;
   align-items: center;
   gap: 11px;
   min-height: 46px;
   padding: 8px 12px;
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-radius: 10px;
   background: #fff;
   color: #111;
   font-size: 13.5px;
   font-weight: 500;
   line-height: 1.3;
   text-decoration: none;
   transition: background 140ms ease, border-color 140ms ease;

   &:hover {
      background: rgba(64, 152, 239, 0.08);
      border-color: ${PRIMARY};
      color: #111;
      text-decoration: none;
   }

   &:focus-visible {
      outline: none;
      border-color: ${PRIMARY};
      box-shadow: ${FOCUS_RING};
   }

   .item-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #5b5b5b;
   }

   .item-label {
      flex: 1;
      min-width: 0;
   }

   .item-new-dot {
      position: relative;
      flex-shrink: 0;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${PRIMARY};
   }

   .sr-only {
      ${srOnly}
   }
`;

// ─── Resultados de busca ────────────────────────────────────────────────────
export const ResultsCard = styled.div`
   background: #fff;
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-radius: 12px;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
   padding: 8px;

   ${enterAnimation}
`;

export const ResultRow = styled(Link)`
   display: flex;
   align-items: center;
   gap: 11px;
   min-height: 46px;
   padding: 8px 12px;
   border: 1px solid transparent;
   border-radius: 10px;
   color: #111;
   font-size: 13.5px;
   font-weight: 500;
   text-decoration: none;
   transition: background 140ms ease, border-color 140ms ease;

   &:hover {
      background: rgba(64, 152, 239, 0.08);
      border-color: ${PRIMARY};
      color: #111;
      text-decoration: none;
   }

   &:focus-visible {
      outline: none;
      border-color: ${PRIMARY};
      box-shadow: ${FOCUS_RING};
   }

   .result-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #5b5b5b;
   }

   .result-label {
      flex: 1;
      min-width: 0;
   }

   .result-path {
      flex-shrink: 0;
      font-size: 12px;
      font-weight: 500;
      color: #8a8a8a;
      text-align: right;
   }

   .sr-only {
      ${srOnly}
   }

   /* Escalonamento curto: as primeiras linhas entram em cascata, dando ao olho
      a leitura de "a lista se formou" em vez de "a tela piscou". Só as 6
      primeiras — além disso o atraso acumulado passaria a parecer lentidão. */
   ${enterAnimation}
   &:nth-child(2) {
      animation-delay: 30ms;
   }
   &:nth-child(3) {
      animation-delay: 60ms;
   }
   &:nth-child(4) {
      animation-delay: 90ms;
   }
   &:nth-child(5) {
      animation-delay: 120ms;
   }
   &:nth-child(n + 6) {
      animation-delay: 150ms;
   }

   @media (max-width: 700px) {
      align-items: flex-start;
      flex-wrap: wrap;

      .result-path {
         width: 100%;
         padding-left: 29px;
         text-align: left;
      }
   }
`;

// Realce do trecho que casou com a busca. Fundo azul claro na mesma família do
// bloco de ícone dos cards (rgba(64,152,239,·)), um pouco mais denso por ficar
// atrás de texto — o texto permanece #111, sem recolorir, para não competir
// com a semântica de cor do FC7.
export const Highlight = styled.mark`
   background: rgba(64, 152, 239, 0.24);
   color: inherit;
   border-radius: 3px;
   padding: 0 1px;
`;

export const ResultsFooter = styled.p`
   margin: 6px 0 0;
   padding: 6px 12px 2px;
   font-size: 12px;
   color: #8a8a8a;
`;

// Aviso de "mín. N caracteres".
//
// Fica SEMPRE montado e colapsa por `grid-template-rows: 0fr → 1fr` em vez de
// entrar e sair do DOM (2026-08-22). Duas razões: a altura anima de verdade
// (aparecer/sumir deixa de ser um salto seco) e nada abaixo do campo pula,
// porque a transição é contínua em vez de um reflow instantâneo.
export const SearchHint = styled.div`
   display: grid;
   grid-template-rows: ${({ $visible }) => ($visible ? '1fr' : '0fr')};
   opacity: ${({ $visible }) => ($visible ? 1 : 0)};
   transition:
      grid-template-rows 220ms ease,
      opacity 180ms ease;

   > p {
      overflow: hidden;
      margin: 0;
      padding-top: ${({ $visible }) => ($visible ? '6px' : '0')};
      font-size: 12px;
      color: #a9a9a9;
      transform: translateY(${({ $visible }) => ($visible ? '0' : '-3px')});
      transition:
         padding-top 220ms ease,
         transform 220ms ease;
   }

   @media (prefers-reduced-motion: reduce) {
      transition: none;

      > p {
         transition: none;
         transform: none;
      }
   }
`;

export const ResultsEmpty = styled.div`
   padding: 28px 16px;
   text-align: center;

   h3 {
      margin: 0 0 6px;
      font-size: 15px;
      font-weight: 700;
      color: #111;
   }

   p {
      margin: 0;
      font-size: 13.5px;
      color: #666;
   }
`;

// ─── Últimas telas (chips escuros) ──────────────────────────────────────────
export const ChipRow = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 8px;
`;

export const Chip = styled(Link)`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   min-height: 36px;
   padding: 0 14px;
   border-radius: 999px;
   border: 1px solid rgba(255, 255, 255, 0.16);
   background: rgba(255, 255, 255, 0.04);
   color: #e5e5e5;
   font-size: 13px;
   font-weight: 500;
   text-decoration: none;
   transition: background 140ms ease, border-color 140ms ease;

   &:hover {
      background: rgba(255, 255, 255, 0.09);
      border-color: rgba(255, 255, 255, 0.3);
      color: #fff;
      text-decoration: none;
   }

   &:focus-visible {
      outline: none;
      border-color: ${PRIMARY};
      box-shadow: ${FOCUS_RING};
   }

   svg {
      flex-shrink: 0;
      color: #bfbfbf;
   }
`;

export const RecentsBlock = styled.div`
   margin-top: 30px;
`;

// Anima na volta da busca também: limpar o campo devolve a grade, e sem
// transição essa volta é tão seca quanto a ida.
export const ModulesBlock = styled.div`
   margin-top: 4px;

   ${enterAnimation}
`;

// Nenhum módulo liberado para o perfil.
export const EmptyState = styled.div`
   background: #fff;
   border: 1px solid rgba(0, 0, 0, 0.08);
   border-radius: 12px;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
   padding: 32px 24px;
   text-align: center;

   h3 {
      margin: 0 0 8px;
      font-size: 16px;
      font-weight: 700;
      color: #111;
   }

   p {
      margin: 0 auto;
      max-width: 46ch;
      font-size: 13.5px;
      line-height: 1.5;
      color: #666;
   }
`;
